import { iterateBuildingGeometries } from "building-geometry/geometry/buildingGeometry";
import { collectBuildingProps } from "building-geometry/props/collectBuildingProps";
import type { Building } from "building-types/building/buildingTypes";
import { lineRenderGeometryOptionsNew } from "building-types/geometry/buildingGeometryTypes";
import type { FaceIteratorParams } from "building-types/geometry/faceIteratorTypes";
import type { FaceCategory } from "building-types/materials/faceCategories";
import { defaultIterateFaceCategories } from "building-types/materials/faceCategories";
import type { PropWithAssetId } from "building-types/props/propTypes";
import type { ViewEntityTable } from "building-types/views/viewEntityTable";
import type { RWTable, Table } from "common/data/table";
import { DesignMaterialPath } from "common/materials/library/designMaterialPath";
import { DMC } from "common/materials/library/DMC";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialSrcFromPalette } from "doc/materialLookup";
import { filter3DProp } from "doc/plans/sheetGeometryEffects";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import type { Mat4 } from "geom/mat4";
import { UVGeneration } from "geom/uv";
import { threeBufferGeometryToMesh3 } from "gfx/convert";
import type { FaceGroupBSP } from "lib/house/faceGroupBSP";
import { faceGroupBSPNew } from "lib/house/faceGroupBSP";
import { collectPropSubmeshesAndMaterials, PropSubmeshMaterialType } from "lib/house/materialMapping";
import {
  getFoundationFaceReportingUnitResolver,
  resolveFoundationFaceReportingUnitLabelId,
} from "lib/house/plans/reportingUnitFoundationAttribution";
import { loadPropNoMaterials } from "lib/house/render/utils";
import { BufferGeometry, Matrix4 } from "three";
import type { CollectedBuildingMesh } from "./perspectiveTypes";

interface CollectBuildingFacesOptions {
  building: Building;
  buildMask?: Set<FaceCategory>;
  enableHatchMaterials?: boolean;
  entityTable?: ViewEntityTable | undefined;
  addEntityIds?: boolean;
}

type CollectFacesCallBack = (props: { faceGroup: FaceGroupBSP; faceCategory: FaceCategory; entityId?: string }) => void;

/**
 * This returns the generated building geometry. It excludes props. Props are processed in collectBuildingMeshes, which in turn calls collectBuildingProps.
 */
function collectBuildingFacesInternal({
  building,
  buildMask = defaultIterateFaceCategories,
  enableHatchMaterials = true,
  entityTable = undefined,
  addEntityIds = false,
  callBack,
}: CollectBuildingFacesOptions & {
  callBack: CollectFacesCallBack;
}): void {
  // Depends on the building alone, so collected groups stay reusable across unit filters.
  const foundationReportingUnitResolver = getFoundationFaceReportingUnitResolver(building);

  function iterateFace({
    faceCategory,
    faces,
    material,
    uvGen = UVGeneration.GLOBAL_RECT_UV,
    entityId = undefined,
    materialTransformation = undefined,
    generateNeighbors = undefined,
    entityHandleForSelectingReportingUnit = undefined,
    reportingUnitLabelId = undefined,
  }: FaceIteratorParams) {
    const faceGroup = faceGroupBSPNew(
      faces,
      enableHatchMaterials && material ? material : null,
      uvGen,
      materialTransformation,
      entityId,
      generateNeighbors,
      entityHandleForSelectingReportingUnit,
      resolveFoundationFaceReportingUnitLabelId(
        foundationReportingUnitResolver,
        faceCategory,
        faces,
        reportingUnitLabelId,
      ) ?? undefined,
    );

    callBack({ faceGroup, faceCategory, entityId });
  }

  iterateBuildingGeometries(building, buildMask, lineRenderGeometryOptionsNew(addEntityIds, entityTable), iterateFace);
}

export function collectBuildingFacesByCategory(props: CollectBuildingFacesOptions): Table<FaceGroupBSP[]> {
  const result: RWTable<FaceGroupBSP[]> = {};

  const callBack: CollectFacesCallBack = ({ faceGroup, faceCategory }) => {
    const category = result[faceCategory] ?? (result[faceCategory] = []);
    category.push(faceGroup);
  };

  collectBuildingFacesInternal({ ...props, callBack });

  return result;
}

export function collectBuildingFacesByEntityId(props: CollectBuildingFacesOptions): Table<FaceGroupBSP[]> {
  const result: RWTable<FaceGroupBSP[]> = {};

  const callBack: CollectFacesCallBack = ({ faceGroup, entityId }) => {
    const category = result[entityId ?? "undefined"] ?? (result[entityId ?? "undefined"] = []);
    category.push(faceGroup);
  };

  collectBuildingFacesInternal({ ...props, callBack });

  return result;
}

function invertTriangles(indices: Uint32Array) {
  for (let i = 0, l = indices.length; i < l; i += 3) {
    const tmp = indices[i];
    indices[i] = indices[i + 2];
    indices[i + 2] = tmp;
  }
}

function matrix4ToMat4(matrix: Matrix4): Mat4 {
  return Array.from(matrix.elements);
}

export enum MeshNamingConvention {
  UNIQUE_NAMES,
  EXPORT,
}

export enum PropMeshTransformSpace {
  WORLD,
  PROP_LOCAL,
}

interface CollectBuildingMeshesParams {
  building: Building;
  faceCategoryFilter?: Set<FaceCategory>;
  visibilitySelectors?: SheetGeometrySelectorEffectMap[];
  propsForElevationOrPerspectiveView?: boolean;
  findMaterials?: boolean;
  entityTable?: ViewEntityTable;
  addEntityIds?: boolean;
  meshNamingConvention: MeshNamingConvention;
  propMeshTransformSpace?: PropMeshTransformSpace;
  /**
   * Optional caller-supplied predicate to keep only a subset of props (e.g. filtering to a single
   * reporting unit). Domain-specific selection stays in the caller so this collector stays generic.
   */
  propFilter?: (prop: PropWithAssetId) => boolean;
}

export async function collectBuildingMeshes({
  building,
  faceCategoryFilter,
  visibilitySelectors,
  findMaterials = false,
  entityTable = undefined,
  addEntityIds = false,
  meshNamingConvention,
  propMeshTransformSpace = PropMeshTransformSpace.WORLD,
  propFilter,
}: CollectBuildingMeshesParams): Promise<{ meshes: CollectedBuildingMesh[]; materials: (MaterialSrc | undefined)[] }> {
  const propCatalog = building.settings.props;

  let props = collectBuildingProps(building, faceCategoryFilter);
  if (visibilitySelectors) {
    props = props.filter((prop) => filter3DProp({ prop, selectors: visibilitySelectors }));
  }
  if (propFilter) {
    props = props.filter(propFilter);
  }

  if (addEntityIds && entityTable) {
    for (const prop of props) {
      const { descriptor } = prop;
      if (!descriptor) {
        continue;
      }
      entityTable.insert(descriptor.entityId, descriptor);
    }
  }

  const propMeshesAndMaterials = await Promise.all(
    props.map(async (prop, propIndex) => {
      if (faceCategoryFilter && !faceCategoryFilter.has(prop.faceCategory)) {
        return { meshes: [], materials: [] };
      }

      const { exteriorPalette, interiorPalette } = building;
      const meshes: CollectedBuildingMesh[] = [];
      const materials: (MaterialSrc | undefined)[] = [];

      const [object, propName = "unnamedProp"] = await loadPropNoMaterials(prop);

      if (!object) {
        return { meshes, materials };
      }

      object.updateMatrixWorld(true);

      const rootTransform = object.matrixWorld.clone();
      const propRootTransform =
        propMeshTransformSpace === PropMeshTransformSpace.PROP_LOCAL ? matrix4ToMat4(rootTransform) : undefined;
      const rootTransformInverse =
        propMeshTransformSpace === PropMeshTransformSpace.PROP_LOCAL ? rootTransform.clone().invert() : undefined;
      const propInstanceId = prop.descriptor?.entityId ?? `prop_${propIndex}`;

      let submeshNameIndex = 0;

      const propSubmeshes = collectPropSubmeshesAndMaterials(
        prop,
        object,
        exteriorPalette,
        interiorPalette,
        undefined,
        propCatalog,
      );

      for (const propSubmesh of propSubmeshes) {
        propSubmesh.obj.updateMatrixWorld(true);

        const geoCopy = new BufferGeometry();
        geoCopy.copy(propSubmesh.obj.geometry as BufferGeometry);

        let meshTransform: Matrix4;
        if (propMeshTransformSpace === PropMeshTransformSpace.PROP_LOCAL) {
          if (!rootTransformInverse) {
            throw new Error("Expected prop root transform inverse for prop-local export");
          }
          meshTransform = new Matrix4().multiplyMatrices(rootTransformInverse, propSubmesh.obj.matrixWorld);
        } else {
          meshTransform = propSubmesh.obj.matrixWorld;
        }

        geoCopy.applyMatrix4(meshTransform);

        if (meshTransform.determinant() < 0 && geoCopy.index) {
          invertTriangles(geoCopy.index.array as Uint32Array);
        }

        let meshName = "";
        if (meshNamingConvention === MeshNamingConvention.EXPORT) {
          meshName = propName;
          if (submeshNameIndex > 0) {
            meshName += "_subMesh" + submeshNameIndex;
          }
        }
        ++submeshNameIndex;

        const mesh = threeBufferGeometryToMesh3(geoCopy, { normal: true, uv: true, uv1: false, id: false });
        meshes.push({
          mesh,
          faceCategory: prop.faceCategory,
          name: meshName,
          // Hatch UVs must match the 3D renderers, which sample the authored gltf uv attribute (SDO-6043).
          useMeshUv: !!mesh.uvs1,
          id: addEntityIds && entityTable ? prop.descriptor?.entityId : undefined,
          propInstanceId,
          propRootName: propName,
          propRootTransform,
          propMirrorX: prop.mirrorPropX ?? prop.scaleX < 0,
          propMirrorY: prop.mirrorPropY ?? prop.scaleY < 0,
        });

        if (findMaterials) {
          let meshMaterial: MaterialSrc | undefined;
          switch (propSubmesh.materialType) {
            case PropSubmeshMaterialType.DEFAULT:
              meshMaterial = {
                ...DesignMaterialPath.Color.C,
                colorId: DMC.SystemColors.Light_Cream,
              };
              break;
            case PropSubmeshMaterialType.PALETTE_SLOT:
              meshMaterial = materialSrcFromPalette(
                propSubmesh.useExteriorPalette ? exteriorPalette : interiorPalette,
                propSubmesh.paletteSlotId,
                prop.room?.type,
              );
              break;
            case PropSubmeshMaterialType.MATERIAL_SRC:
              meshMaterial = propSubmesh.materialSrc;
              break;
          }
          materials.push(meshMaterial);
        }
      }

      return { meshes, materials };
    }),
  );

  const meshes = propMeshesAndMaterials.flatMap((result) => result.meshes);
  const materials = propMeshesAndMaterials.flatMap((result) => result.materials);

  return {
    // Assigning UNIQUE_NAMES indices after the Promise.all resolves keeps the
    // naming deterministic regardless of the order in which the async prop
    // loading operations above complete.
    meshes:
      meshNamingConvention === MeshNamingConvention.UNIQUE_NAMES
        ? meshes.map((mesh, index) => ({ ...mesh, name: index.toString() }))
        : meshes,
    materials,
  };
}
